import { AppwriteException, ID, Permission, Query, Role } from "appwrite";
import { z } from "zod";
import {
	AppwriteClient,
	getAppwriteClient,
	getCurrentUser,
} from "$/modules/appwrite/lib/appwrite";
import { collections } from "$/modules/appwrite/lib/appwrite-collections";
import {
	ExamIdentityMode,
	type Project,
	ProjectRole,
	type Question,
	type Questionnaire,
	QuizType,
	ShareMode,
} from "$/modules/appwrite/types/collections";
import { getTranslations } from "$/modules/i18n/lib/i18n";
import { decodeTextEscapes } from "$/modules/shared/lib/text-normalization";
import type { ServerActionResponse } from "$/modules/shared/types/serverActionResponse";
import {
	requireProjectRole,
	syncProjectCollaborationPermissions,
} from "./project-access";

export const getAllProjects = async (
	authenticatedUserId?: string,
): Promise<Project[]> => {
	const userId = authenticatedUserId ?? (await getCurrentUser())?.$id;
	const appwrite = getAppwriteClient();

	if (!userId) {
		throw new Error("Unauthenticated");
	}

	try {
		const projects = await appwrite.tablesDB.listRows<Project>(
			AppwriteClient.databaseId,
			collections.projects,
			[
				Query.contains("userId", userId),
				Query.select(["*", "questionnaires.$id"]),
			],
		);

		const memberships = await appwrite.tablesDB.listRows<
			import("$/modules/appwrite/types/collections").ProjectMember
		>(AppwriteClient.databaseId, collections.projectMembers, [
			Query.equal("userId", userId),
			Query.limit(100),
		]);
		const accessibleProjects = await Promise.all(
			memberships.rows.map((membership) =>
				appwrite.tablesDB
					.getRow<Project>(
						AppwriteClient.databaseId,
						collections.projects,
						membership.projectId,
						[Query.select(["*", "questionnaires.$id"])],
					)
					.catch(() => null),
			),
		);
		const unique = new Map(
			projects.rows.map((project) => [project.$id, project]),
		);
		for (const project of accessibleProjects)
			if (project) unique.set(project.$id, project);
		return [...unique.values()];
	} catch (error) {
		if (error instanceof AppwriteException) {
			console.error("Error fetching projects:", error.message);
		}
		throw error;
	}
};

const getProjectByIdInputSchema = z.object({
	projectId: z.string(),
});

export const getProjectById = async (
	data: z.infer<typeof getProjectByIdInputSchema>,
) => {
	const appwrite = getAppwriteClient();

	const parsedData = getProjectByIdInputSchema.parse(data);

	return await appwrite.tablesDB.getRow<Project>(
		AppwriteClient.databaseId,
		collections.projects,
		parsedData.projectId,
		[Query.select(["*", "questionnaires.*", "questionnaires.questions.*"])],
	);
};

export const projectSchema = z.object({
	name: z
		.string()
		.transform(decodeTextEscapes)
		.pipe(z.string().trim().min(3).max(255)),
	description: z.preprocess(
		(value) =>
			typeof value === "string" ? decodeTextEscapes(value) || null : value,
		z.string().trim().max(5_000).nullable(),
	),
	quizType: z.enum(QuizType),
	examPassingScore: z.coerce.number().int().min(0).max(100).default(70),
	examIdentityMode: z.enum(ExamIdentityMode).default(ExamIdentityMode.Guest),
	examMaxAttempts: z.coerce.number().int().min(1).max(100).default(1),
});

const examConfigurationSchema = z.object({
	examPassingScore: z.coerce.number().int().min(0).max(100).optional(),
	examIdentityMode: z.enum(ExamIdentityMode).optional(),
	examMaxAttempts: z.coerce.number().int().min(1).max(100).optional(),
});

const shareProjectSchema = z.object({
	projectId: z.string(),
	mode: z.enum([ShareMode.Public, ShareMode.Members, ShareMode.Invited]),
	emails: z.array(z.string().email()).default([]),
	mockEmails: z.array(z.string().email()).default([]),
});

const ownerPermissions = (ownerId: string) => [
	Permission.read(
		Role.user(ownerId.replace(/^user:/, "").replace(/\/verified$/, "")),
	),
	Permission.update(
		Role.user(ownerId.replace(/^user:/, "").replace(/\/verified$/, "")),
	),
	Permission.delete(
		Role.user(ownerId.replace(/^user:/, "").replace(/\/verified$/, "")),
	),
];

const readPermission = (
	mode: ShareMode,
	teamId?: string | null,
	missingTeamMessage = "Private invite mode requires a team.",
) => {
	if (mode === ShareMode.Public) return Permission.read(Role.any());
	if (mode === ShareMode.Members) return Permission.read(Role.users());
	if (!teamId) throw new Error(missingTeamMessage);
	return Permission.read(Role.team(teamId));
};

const sharePermissions = (
	ownerId: string,
	mode: ShareMode,
	teamId?: string | null,
	missingTeamMessage?: string,
) => [
	...ownerPermissions(ownerId),
	readPermission(mode, teamId, missingTeamMessage),
];

const _updateProjectContentPermissions = async (
	projectId: string,
	permissions: string[],
) => {
	const appwrite = getAppwriteClient();
	const questionnaires = await appwrite.tablesDB.listRows<Questionnaire>(
		AppwriteClient.databaseId,
		collections.questionnaires,
		[Query.equal("project", projectId)],
	);

	await Promise.all(
		questionnaires.rows.map(async (questionnaire) => {
			await appwrite.tablesDB.updateRow(
				AppwriteClient.databaseId,
				collections.questionnaires,
				questionnaire.$id,
				{},
				permissions,
			);

			const questions = await appwrite.tablesDB.listRows<Question>(
				AppwriteClient.databaseId,
				collections.questions,
				[Query.equal("questionnaire", questionnaire.$id)],
			);

			await Promise.all(
				questions.rows.map((question) =>
					appwrite.tablesDB.updateRow(
						AppwriteClient.databaseId,
						collections.questions,
						question.$id,
						{},
						permissions,
					),
				),
			);
		}),
	);
};

export const shareProject = async (
	data: z.infer<typeof shareProjectSchema>,
) => {
	const t = await getTranslations("share");
	const parsedData = shareProjectSchema.parse(data);
	const appwrite = getAppwriteClient();
	const user = await getCurrentUser();

	if (!user) throw new Error("Unauthenticated");

	const project = await appwrite.tablesDB.getRow<Project>(
		AppwriteClient.databaseId,
		collections.projects,
		parsedData.projectId,
	);

	await requireProjectRole(project, ProjectRole.Admin);

	let teamId = project.shareTeamId;
	if (
		parsedData.mode === ShareMode.Invited ||
		parsedData.mockEmails.length > 0
	) {
		if (!teamId) {
			const team = await appwrite.teams.create(
				ID.unique(),
				`Quiz: ${project.name}`,
			);
			teamId = team.$id;
		}
		const inviteTeamId = teamId;

		await Promise.all(
			parsedData.emails.map((email) =>
				appwrite.teams.createMembership(
					inviteTeamId,
					["viewer"],
					email,
					undefined,
					undefined,
					`${window.location.origin}/`,
				),
			),
		);
	}

	const permissions = sharePermissions(
		project.userId[0],
		parsedData.mode,
		teamId,
		t("privateInviteTeamRequired"),
	);
	await appwrite.tablesDB.updateRow(
		AppwriteClient.databaseId,
		collections.projects,
		project.$id,
		{ shareMode: parsedData.mode, shareTeamId: teamId ?? null },
		permissions,
	);
	await syncProjectCollaborationPermissions({
		...project,
		shareMode: parsedData.mode,
		shareTeamId: teamId ?? null,
	});

	return {
		success: true,
		message: t("updated"),
		mode: parsedData.mode,
	};
};

export const createProject = async (data: z.infer<typeof projectSchema>) => {
	const parsedData = projectSchema.parse(data);
	const t = await getTranslations("project.message");

	try {
		await executeProjectAction({
			action: "createProject",
			...parsedData,
			locale: document.documentElement.lang,
		});
	} catch (error) {
		if (error instanceof Error && error.message.startsWith("invalid")) {
			throw new Error(t(`create.${error.message}`));
		}
		if (error instanceof Error && error.message === "license.limitReached") {
			throw new Error(t("create.licenseLimitReached"));
		}
		throw error;
	}

	return {
		message: t("create.success"),
		success: true,
	};
};

export type LicenseStatus = {
	plan: "free" | "studio";
	ownedProjectCount: number;
	projectLimit: number | null;
	canCreateProject: boolean;
};

const projectFunctionId =
	import.meta.env.VITE_APPWRITE_EXAM_FUNCTION_ID || "exam-manager";

const executeProjectAction = async <T>(
	body: Record<string, unknown>,
): Promise<T> => {
	const execution = await getAppwriteClient().functions.createExecution(
		projectFunctionId,
		JSON.stringify(body),
		false,
	);
	const response = JSON.parse(execution.responseBody || "{}") as
		| (T & { error?: string })
		| { data?: T; error?: string };
	if (response.error) throw new Error(response.error);
	return ("data" in response && response.data ? response.data : response) as T;
};

const syncProjectEditorAccessSchema = z.object({
	projectId: z.string(),
});

/** Synchronizes document and media permissions around direct content saves. */
export const syncProjectEditorAccess = async (
	data: z.infer<typeof syncProjectEditorAccessSchema>,
) => {
	const parsedData = syncProjectEditorAccessSchema.parse(data);
	return executeProjectAction<{ success: true }>({
		action: "syncProjectEditorAccess",
		...parsedData,
	});
};

const createSharedQuestionnaireSchema = z.object({
	projectId: z.string(),
	name: z.string().trim().min(3).max(120),
	previewImage: z.string().nullable(),
});

export const createSharedQuestionnaire = async (
	data: z.infer<typeof createSharedQuestionnaireSchema>,
) => {
	const parsedData = createSharedQuestionnaireSchema.parse(data);
	return executeProjectAction<{ success: true }>({
		action: "createSharedQuestionnaire",
		...parsedData,
	});
};

const editSharedQuestionnaireSchema = z.object({
	questionnaireId: z.string(),
	name: z.string().trim().min(3).max(120),
	previewImage: z.string().nullable(),
	replacePreviewImage: z.boolean(),
});

export const editSharedQuestionnaire = async (
	data: z.infer<typeof editSharedQuestionnaireSchema>,
) => {
	const parsedData = editSharedQuestionnaireSchema.parse(data);
	return executeProjectAction<{ success: true }>({
		action: "editSharedQuestionnaire",
		...parsedData,
	});
};

const deleteSharedQuestionnaireSchema = z.object({
	questionnaireId: z.string(),
});

export const deleteSharedQuestionnaire = async (
	data: z.infer<typeof deleteSharedQuestionnaireSchema>,
) => {
	const parsedData = deleteSharedQuestionnaireSchema.parse(data);
	return executeProjectAction<{ success: true }>({
		action: "deleteSharedQuestionnaire",
		...parsedData,
	});
};

const deleteSharedQuestionSchema = z.object({
	questionId: z.string(),
});

export const deleteSharedQuestion = async (
	data: z.infer<typeof deleteSharedQuestionSchema>,
) => {
	const parsedData = deleteSharedQuestionSchema.parse(data);
	return executeProjectAction<{ success: true }>({
		action: "deleteSharedQuestion",
		...parsedData,
	});
};

export const getLicenseStatus = async (): Promise<LicenseStatus> =>
	executeProjectAction<LicenseStatus>({ action: "getLicenseStatus" });

export const exportQuiz = async (projectId: string) =>
	executeProjectAction<{ file: string }>({ action: "exportQuiz", projectId });

export const importQuiz = async (file: string, nameOverride?: string) =>
	executeProjectAction<{ projectId: string }>({
		action: "importQuiz",
		file,
		nameOverride,
	});

export const updateProjectSchema = projectSchema
	.omit({
		examPassingScore: true,
		examIdentityMode: true,
		examMaxAttempts: true,
	})
	.extend({
		id: z.string(),
	})
	.merge(examConfigurationSchema);

export const updateProject = async (
	data: z.infer<typeof updateProjectSchema>,
) => {
	const appwrite = getAppwriteClient();
	const parsedData = updateProjectSchema.parse(data);
	const user = await getCurrentUser();
	const t = await getTranslations("project.message");

	if (!user) {
		throw new Error(t("create.unauthenticated"));
	}

	const existingProject = await appwrite.tablesDB.getRow<Project>(
		AppwriteClient.databaseId,
		collections.projects,
		parsedData.id,
	);
	await requireProjectRole(existingProject, ProjectRole.Editor);

	const examConfiguration = {
		examPassingScore:
			parsedData.examPassingScore ?? existingProject.examPassingScore ?? 70,
		examIdentityMode:
			parsedData.examIdentityMode ??
			existingProject.examIdentityMode ??
			ExamIdentityMode.Guest,
		examMaxAttempts:
			parsedData.examMaxAttempts ?? existingProject.examMaxAttempts ?? 1,
	};

	await appwrite.tablesDB.updateRow<Project>(
		AppwriteClient.databaseId,
		collections.projects,
		parsedData.id,
		{
			name: parsedData.name,
			description: parsedData.description,
			quizType: parsedData.quizType,
			...examConfiguration,
			done: false,
		},
	);

	return {
		message: t("create.success"),
		success: true,
	};
};

const updateProjectQuizTypeSchema = z.object({
	id: z.string(),
	quizType: z.enum(QuizType),
});

const updateProjectExamSettingsSchema = z.object({
	id: z.string(),
	examPassingScore: z.coerce.number().int().min(0).max(100),
	examIdentityMode: z.enum(ExamIdentityMode),
	examMaxAttempts: z.coerce.number().int().min(1).max(100),
});

/** Update only the rules that govern an existing exam project. */
export const updateProjectExamSettings = async (
	data: z.infer<typeof updateProjectExamSettingsSchema>,
) => {
	const parsedData = updateProjectExamSettingsSchema.parse(data);
	const appwrite = getAppwriteClient();
	const user = await getCurrentUser();
	if (!user) throw new Error("Unauthenticated");

	const project = await appwrite.tablesDB.getRow<Project>(
		AppwriteClient.databaseId,
		collections.projects,
		parsedData.id,
	);
	await requireProjectRole(project, ProjectRole.Editor);
	if (project.quizType !== QuizType.Exam) {
		throw new Error("Prüfungsregeln sind nur für Prüfungsprojekte verfügbar.");
	}

	await appwrite.tablesDB.updateRow<Project>(
		AppwriteClient.databaseId,
		collections.projects,
		project.$id,
		{
			examPassingScore: parsedData.examPassingScore,
			examIdentityMode: parsedData.examIdentityMode,
			examMaxAttempts: parsedData.examMaxAttempts,
		},
	);

	return {
		success: true,
		message: "Prüfungsregeln aktualisiert.",
	};
};

/** Change the project mode without requiring unrelated settings to be edited. */
export const updateProjectQuizType = async (
	data: z.infer<typeof updateProjectQuizTypeSchema>,
) => {
	const parsedData = updateProjectQuizTypeSchema.parse(data);
	const appwrite = getAppwriteClient();
	const user = await getCurrentUser();
	if (!user) throw new Error("Unauthenticated");
	const project = await appwrite.tablesDB.getRow<Project>(
		AppwriteClient.databaseId,
		collections.projects,
		parsedData.id,
	);
	await requireProjectRole(project, ProjectRole.Editor);
	await appwrite.tablesDB.updateRow<Project>(
		AppwriteClient.databaseId,
		collections.projects,
		project.$id,
		{ quizType: parsedData.quizType },
	);
	return {
		success: true,
		message: "Quiz-Typ aktualisiert.",
		quizType: parsedData.quizType,
	};
};

export const deleteProjectSchema = z.object({
	id: z.string(),
});

export const deleteProject = async (
	data: z.infer<typeof deleteProjectSchema>,
): Promise<ServerActionResponse> => {
	const t = await getTranslations("project.message");
	const parsedData = deleteProjectSchema.parse(data);
	await executeProjectAction({
		action: "deleteProject",
		projectId: parsedData.id,
	});

	return {
		message: t("delete.success"),
		success: true,
	};
};
